import { NavLink, useNavigate, useParams } from "react-router";
import t from "@/lang/lang";
import {
  useHtzAirHandlingUnitPutMutation,
  useHtzAirHandlingUnitShowQuery,
} from "@/services/backend/htz/ahu/api/ahu-api";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { AhuBaseDataCard } from "@/services/backend/htz/ahu/components/ahu-base-data-card";
import { H3 } from "@/shared/components/typography";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { AirHandlingUnit } from "@/services/backend/htz/ahu/types/air-handling-unit";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { CardSkeleton } from "@/shared/components/card-skeleton";
import {
  AhuDataTable,
  AhuDataTableFilters,
} from "@/services/backend/htz/ahu/components/ahu-data-table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { ListRequest } from "@/services/backend/htz/ahu/api/ahu-data-api";
import { AhuName } from "@/services/backend/htz/ahu/components/ahu-name";
import { DeleteAhuDialog } from "@/services/backend/htz/ahu/components/delete-ahu-dialog";
import { CustomerPageBreadcrumbs } from "../../../_components/customer-page-breadcrumbs";
import {
  CustomerPageMain,
  CustomerPageMainContent,
  CustomerPageMainHeader,
} from "../../../_components/customer-page";
import { UUID } from "@bitory-gmbh/nidavellir";

export function CustomerHtzAhuRoute() {
  const { customerId, airHandlingUnitId } = useParams();
  const { data, isLoading, error } = useHtzAirHandlingUnitShowQuery({
    id: airHandlingUnitId!,
  });
  const navigate = useNavigate();
  const doNavigateToAhuList = () => {
    navigate(`/customers/${customerId}/htz/ahus`);
  };

  const breadcrumbs = useMemo(
    () => [
      {
        to: `/customers/${customerId}/htz/ahus`,
        label: t("HTZ"),
      },
      {
        to: `/customers/${customerId}/htz/ahus`,
        label: t("RLT-Anlagen"),
      },
      {
        to: `/customers/${customerId}/htz/ahus/${airHandlingUnitId}`,
        label: <AhuName airHandlingUnitId={airHandlingUnitId!} />,
      },
    ],
    [customerId, airHandlingUnitId],
  );

  return (
    <CustomerPageMain>
      <CustomerPageMainHeader>
        <CustomerPageBreadcrumbs breadcrumbs={breadcrumbs} />
      </CustomerPageMainHeader>
      <CustomerPageMainContent className="gap-2">
        <PersistenceContextProvider>
          <RTKQueryErrorAlert error={error} />
          <div className="flex items-center justify-between">
            <H3>
              <span className="mr-2">{t("RLT-Anlage:")}</span>
              <AhuName airHandlingUnitId={airHandlingUnitId!} />
            </H3>
            <div className="flex gap-2">
              <PersistenceStateUsingContext />
              <DeleteAhuDialog
                airHandlingUnitId={airHandlingUnitId!}
                onSuccess={doNavigateToAhuList}
              />
            </div>
          </div>
          {isLoading && <CardSkeleton />}
          {data && <AhuBaseData airHandlingUnit={data} />}
          {data && (
            <RelatedAhuDataInstances
              companyId={data.companyId}
              customerId={data.customerId}
              airHandlingUnitId={data.id}
            />
          )}
        </PersistenceContextProvider>
      </CustomerPageMainContent>
    </CustomerPageMain>
  );
}

function AhuBaseData({
  airHandlingUnit,
}: {
  airHandlingUnit: AirHandlingUnit;
}) {
  const [request, setRequest] = useState(airHandlingUnit);
  const mutation = useHtzAirHandlingUnitPutMutation();
  const { error } = useDebouncedMutation(
    request,
    setRequest,
    mutation,
    airHandlingUnit,
    // longer delay to make ErrDuplicateName error
    // on partial name input match more unlikely
    { delay: 1000 },
  );

  return (
    <AhuBaseDataCard
      request={request}
      onRequestChange={setRequest}
      error={error}
    />
  );
}

function RelatedAhuDataInstances({
  companyId,
  customerId,
  airHandlingUnitId,
}: {
  companyId: UUID;
  customerId: string;
  airHandlingUnitId: string;
}) {
  const [request, setRequest] = useState<ListRequest>({
    company: {
      active: true,
      values: [companyId],
    },
    customer: {
      active: true,
      values: [customerId],
    },
    airHandlingUnit: {
      active: true,
      values: [airHandlingUnitId],
    },
    orderBy: [{ field: "updatedAt", dsc: true }],
  });
  const renderRowLink = useCallback(
    (ahuDataId: string, children: ReactNode) => (
      <NavLink
        to={`/customers/${customerId}/htz/ahus/${airHandlingUnitId}/data/${ahuDataId}`}
      >
        {children}
      </NavLink>
    ),
    [customerId, airHandlingUnitId],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Verknüpfte RLT-Anlagen-Datensätze")}</CardTitle>
        <CardDescription>
          {t(
            "Wenn eine neue Position auf einem Arbeitsschein oder Bericht erstellt wird, wird ein neuer Datensatz für die RLT-Anlage angelegt. Diese Datensätze sind mit der RLT-Anlage verknüpft, damit sie nachverfolgt werden können. Die Datensätze sind stets isoliert, d.h. Änderungen in einem Datensatz bewirken keine Änderung in einem anderen Datensatz. Das ermöglicht es, Änderungen an der Definition der RLT-Anlage vorzunehmen, ohne dass alte Dokumente beeinflusst werden.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-2">
        <AhuDataTableFilters
          request={request}
          onRequestChange={setRequest}
          hideCustomerFilter
        />
        <AhuDataTable request={request} renderRowLink={renderRowLink} />
      </CardContent>
    </Card>
  );
}
